/**
 * StaffProfile - Staff profile component with locked/editable fields
 *
 * Features:
 * - Locked fields for staff member details (set by HR/admin)
 * - Editable fields for personal preferences
 * - Profile picture upload with preview
 * - Role and department display
 * - Activity status indicator
 */

import React, { useState, useRef } from 'react'
import {
  User,
  Mail,
  Phone,
  Building2,
  Shield,
  Calendar,
  Lock,
  Edit3,
  Camera,
  Save,
  X,
  Check,
  MapPin,
  Briefcase,
  Clock
} from 'lucide-react'

interface StaffData {
  // Locked fields (HR-managed)
  id: string
  employeeId: string
  fullName: string
  email: string
  department: string
  role: string
  clearanceLevel: 'L1' | 'L2' | 'L3' | 'L4' | 'Guardian'
  startDate: string
  manager: string

  // Editable fields
  avatar?: string
  phone?: string
  location?: string
  bio?: string
  timezone?: string
  preferredLanguage?: string
  slackHandle?: string
  githubUsername?: string
  status: 'active' | 'away' | 'busy' | 'offline'
}

interface StaffProfileProps {
  staff?: StaffData
  isOwnProfile?: boolean
  className?: string
  onSave?: (data: Partial<StaffData>) => Promise<void>
}

const defaultStaff: StaffData = {
  id: 'staff_001',
  employeeId: 'LK-2024-0042',
  fullName: 'Alex Chen',
  email: 'alex.chen@lukhas.team',
  department: 'Engineering',
  role: 'Senior Consciousness Engineer',
  clearanceLevel: 'L3',
  startDate: '2024-03-15',
  manager: 'Dr. Maya Patel',
  phone: '+1 (555) 123-4567',
  location: 'San Francisco, CA',
  bio: 'Working on consciousness integration patterns and guardian protocols.',
  timezone: 'America/Los_Angeles',
  preferredLanguage: 'en',
  slackHandle: '@alex.chen',
  githubUsername: 'alexchen-lukhas',
  status: 'active'
}

export default function StaffProfile({
  staff = defaultStaff,
  isOwnProfile = true,
  className = '',
  onSave
}: StaffProfileProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedData, setEditedData] = useState<Partial<StaffData>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [previewAvatar, setPreviewAvatar] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const currentData = { ...staff, ...editedData }

  const handleEdit = () => {
    setIsEditing(true)
    setEditedData({})
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditedData({})
    setPreviewAvatar(null)
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      if (onSave) {
        await onSave(editedData)
      }
      // Simulate save delay for demo
      await new Promise(resolve => setTimeout(resolve, 500))
      setIsEditing(false)
      setPreviewAvatar(null)
    } catch (err) {
      console.error('Failed to save profile:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleChange = (field: keyof StaffData, value: string) => {
    setEditedData(prev => ({ ...prev, [field]: value }))
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        setPreviewAvatar(result)
        setEditedData(prev => ({ ...prev, avatar: result }))
      }
      reader.readAsDataURL(file)
    }
  }

  const triggerAvatarUpload = () => {
    fileInputRef.current?.click()
  }

  const getClearanceBadgeColor = (level: string) => {
    switch (level) {
      case 'Guardian':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
      case 'L4':
        return 'bg-red-500/20 text-red-400 border-red-500/30'
      case 'L3':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30'
      case 'L2':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500'
      case 'away':
        return 'bg-yellow-500'
      case 'busy':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const LockedField = ({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) => (
    <div className="flex items-start space-x-3 p-3 bg-gray-800/30 rounded-lg border border-gray-700/30">
      <div className="flex items-center space-x-2 min-w-[140px]">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-500">{label}</span>
        <Lock className="w-3 h-3 text-gray-600" />
      </div>
      <span className="text-sm text-white flex-1">{value}</span>
    </div>
  )

  const EditableField = ({
    icon: Icon,
    label,
    field,
    value,
    placeholder
  }: {
    icon: React.ElementType
    label: string
    field: keyof StaffData
    value: string
    placeholder?: string
  }) => (
    <div className="flex items-start space-x-3 p-3 bg-gray-800/30 rounded-lg border border-gray-700/30">
      <div className="flex items-center space-x-2 min-w-[140px]">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      {isEditing ? (
        <input
          type="text"
          value={editedData[field] ?? value}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-gray-700/50 border border-gray-600/50 rounded px-2 py-1 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-lambda-gold/50"
        />
      ) : (
        <span className="text-sm text-white flex-1">{value || '-'}</span>
      )}
    </div>
  )

  return (
    <div className={`bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700/50 bg-gray-800/40 flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Staff Profile</h3>
        {isOwnProfile && (
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-lambda-gold/20 text-lambda-gold border border-lambda-gold/30 rounded-lg hover:bg-lambda-gold/30 transition-colors disabled:opacity-50"
                >
                  {isSaving ? (
                    <div className="w-4 h-4 border-2 border-lambda-gold/30 border-t-lambda-gold rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>Save</span>
                </button>
              </>
            ) : (
              <button
                onClick={handleEdit}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-6">
        {/* Avatar and Basic Info */}
        <div className="flex items-start space-x-6 mb-6">
          {/* Avatar */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gray-700/50 border-2 border-gray-600/50 overflow-hidden">
              {(previewAvatar || currentData.avatar) ? (
                <img
                  src={previewAvatar || currentData.avatar}
                  alt={currentData.fullName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-12 h-12 text-gray-500" />
                </div>
              )}
            </div>

            {/* Status indicator */}
            <div className={`absolute bottom-1 right-1 w-5 h-5 rounded-full border-2 border-gray-900 ${getStatusColor(currentData.status)}`} />

            {/* Upload button */}
            {isEditing && isOwnProfile && (
              <button
                onClick={triggerAvatarUpload}
                className="absolute bottom-0 right-0 p-1.5 bg-gray-700 rounded-full border border-gray-600 hover:bg-gray-600 transition-colors"
              >
                <Camera className="w-4 h-4 text-white" />
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>

          {/* Name and Role */}
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-2xl font-semibold text-white">{currentData.fullName}</h2>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getClearanceBadgeColor(currentData.clearanceLevel)}`}>
                {currentData.clearanceLevel}
              </span>
            </div>
            <p className="text-lg text-lambda-gold mb-1">{currentData.role}</p>
            <p className="text-sm text-gray-400">{currentData.department}</p>

            {/* Status selector */}
            {isEditing && isOwnProfile && (
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-sm text-gray-500">Status:</span>
                <select
                  value={editedData.status ?? currentData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="bg-gray-700/50 border border-gray-600/50 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-lambda-gold/50"
                >
                  <option value="active">Active</option>
                  <option value="away">Away</option>
                  <option value="busy">Busy</option>
                  <option value="offline">Offline</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Locked Fields (HR-managed) */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center space-x-2">
            <Lock className="w-4 h-4" />
            <span>Organization Details (HR Managed)</span>
          </h4>
          <div className="space-y-2">
            <LockedField icon={Briefcase} label="Employee ID" value={currentData.employeeId} />
            <LockedField icon={Mail} label="Email" value={currentData.email} />
            <LockedField icon={Building2} label="Department" value={currentData.department} />
            <LockedField icon={User} label="Manager" value={currentData.manager} />
            <LockedField icon={Calendar} label="Start Date" value={new Date(currentData.startDate).toLocaleDateString()} />
            <LockedField icon={Shield} label="Clearance" value={`Level ${currentData.clearanceLevel}`} />
          </div>
        </div>

        {/* Editable Fields */}
        <div>
          <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center space-x-2">
            <Edit3 className="w-4 h-4" />
            <span>Personal Details {isOwnProfile && '(Editable)'}</span>
          </h4>
          <div className="space-y-2">
            <EditableField icon={Phone} label="Phone" field="phone" value={currentData.phone || ''} placeholder="+1 (555) 000-0000" />
            <EditableField icon={MapPin} label="Location" field="location" value={currentData.location || ''} placeholder="City, State" />
            <EditableField icon={Clock} label="Timezone" field="timezone" value={currentData.timezone || ''} placeholder="America/Los_Angeles" />
            <EditableField icon={User} label="Slack" field="slackHandle" value={currentData.slackHandle || ''} placeholder="@username" />
            <EditableField icon={User} label="GitHub" field="githubUsername" value={currentData.githubUsername || ''} placeholder="username" />
          </div>

          {/* Bio */}
          <div className="mt-4">
            <label className="text-sm text-gray-500 block mb-2">Bio</label>
            {isEditing ? (
              <textarea
                value={editedData.bio ?? currentData.bio ?? ''}
                onChange={(e) => handleChange('bio', e.target.value)}
                placeholder="Tell us about yourself..."
                rows={3}
                className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-lambda-gold/50 resize-none"
              />
            ) : (
              <p className="text-sm text-gray-300 bg-gray-800/30 rounded-lg p-3 border border-gray-700/30">
                {currentData.bio || 'No bio provided'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
